import { ArrowForward } from '@mui/icons-material';
import {
    Box,
    Divider,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { BehaviorSubject, debounceTime, tap } from 'rxjs';
import GuardedLink from '../../../../common/auth/GuardedLink';
import { ResourceType } from '../../../../common/PricePrgClient';
import { RootState } from '../../../../store';
import { notNull } from '../../../../utils';
import { includes, startsWith } from '../../../../utils/stringUtils';
import { getCustomers } from '../../../customer/redux/customerSelectors';
import { customerSelected } from '../../../customer/redux/customerSlices';
import {
    getSelectedExternalPricelist,
    getSelectedExternalPricelistId
} from '../../redux/externalPricelistSelectors';

const mapStateToProps = (state: RootState) => {
    return {
        selectedExternalPricelistId: getSelectedExternalPricelistId(state),
        customersProp: getCustomers(state),
        selectedExternalPricelist: getSelectedExternalPricelist(state)
    };
};
const customerFilterSubject = new BehaviorSubject<string>('');

type Props = ReturnType<typeof mapStateToProps>;

const CustomersPricelistTab = ({
    selectedExternalPricelistId,
    customersProp,
    selectedExternalPricelist
}: Props) => {
    const dispatch = useDispatch();

    const customers = useMemo(
        () =>
            Object.values(customersProp)
                .filter(cust => notNull(cust.externalPricelistId))
                .filter(cust => cust.externalPricelistId === selectedExternalPricelistId)
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
        [customersProp, selectedExternalPricelistId]
    );
    const [filteredCustomers, setFilteredCustomers] = useState(customers);

    const [customerFilter, setCustomerFilter] = useState('');

    const handleOnFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        customerFilterSubject.next(event.target.value);
        setCustomerFilter(event.target.value);
    };

    const filterCustomers = useCallback(
        (filter: string) => {
            if (filter.length < 3) {
                return customers;
            }
            return customers.filter(
                cust => includes(filter, cust.name) || startsWith(filter, cust.customerNumber?.toString())
            );
        },
        [customers]
    );

    useEffect(() => {
        setFilteredCustomers(filterCustomers(''));
    }, [filterCustomers]);

    useEffect(() => {
        const subscription = customerFilterSubject
            .pipe(
                debounceTime(200),
                tap(cf => {
                    setFilteredCustomers(filterCustomers(cf));
                })
            )
            .subscribe();
        return () => subscription.unsubscribe();
    }, [filterCustomers, setCustomerFilter]);

    if (selectedExternalPricelistId === null) {
        return (
            <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 400, marginTop: '5%' }}>
                Choose a pricelist to view more details about it.
            </Typography>
        );
    }

    const dispatchGotoSelectedCustomer = (customerId: number) => {
        dispatch(customerSelected(customerId));
    };

    const PricelistDivider = () => (
        <Grid container direction="row">
            <Grid item xs={12} md={12} lg={6}>
                <Divider sx={{ margin: '16px 0' }} orientation="horizontal" variant="fullWidth" />
            </Grid>
        </Grid>
    );

    return (
        <Box component="div" sx={{ width: '60rem' }}>
            <Typography variant="h6">
                Customers linked to pricelist: &quot;{selectedExternalPricelist?.name}&quot;
            </Typography>
            <PricelistDivider />
            <Grid container direction="column" spacing={2}>
                <TextField
                    sx={{ display: 'block', margin: '16px 0' }}
                    label=""
                    onChange={handleOnFilterChange}
                    value={customerFilter}
                    placeholder="Filter"
                    variant="standard"
                />
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Cust.No.</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCustomers.map(cust => (
                            <TableRow key={cust.customerId}>
                                <TableCell>{cust.customerNumber}</TableCell>
                                <TableCell>{cust.name}</TableCell>
                                <TableCell>
                                    <GuardedLink
                                        resource={ResourceType.CustomerPage}
                                        link={'/customers'}
                                        childComponents={
                                            <Tooltip sx={{ display: 'inline-flex' }} title="Go to customer">
                                                <IconButton
                                                    onClick={() =>
                                                        dispatchGotoSelectedCustomer(cust.customerId!)
                                                    }
                                                    size="small"
                                                >
                                                    <ArrowForward />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Grid>
        </Box>
    );
};

export default connect(mapStateToProps)(CustomersPricelistTab);
